// ============================================
// Data Validation & Normalization Utilities
// ============================================

/**
 * Validates and normalizes step data from health collection
 */
export function normalizeStepsData(raw: any): {
  date: string
  steps: number
  distance: number // in km
  calories: number
  activeMinutes: number
} {
  if (!raw || typeof raw !== 'object') {
    return {
      date: '',
      steps: 0,
      distance: 0,
      calories: 0,
      activeMinutes: 0,
    }
  }

  // Distance stored in meters in Firestore, convert to km for display
  const distanceMeters = raw.distance || 0
  
  return {
    date: raw.date || '',
    steps: Math.max(0, Math.round(raw.steps || 0)),
    distance: Math.round((distanceMeters / 1000) * 100) / 100, // 2 decimal places
    calories: Math.max(0, Math.round(raw.calories || 0)),
    activeMinutes: Math.max(0, Math.round(raw.activeMinutes || raw.active_minutes || 0)),
  }
}

/**
 * Validates and normalizes sleep data
 */
export function normalizeSleepData(raw: any): {
  date: string
  totalMinutes: number
  totalDuration: number // alias for charts
  deepMinutes: number
  lightMinutes: number
  remMinutes: number
  awakeMinutes: number
  quality: string | null
} {
  if (!raw || typeof raw !== 'object') {
    return {
      date: '',
      totalMinutes: 0,
      totalDuration: 0,
      deepMinutes: 0,
      lightMinutes: 0,
      remMinutes: 0,
      awakeMinutes: 0,
      quality: null,
    }
  }

  // Handle both naming conventions from mobile app
  const total = raw.totalMinutes || raw.sleepTotal || raw.total_minutes || 0
  
  return {
    date: raw.date || '',
    totalMinutes: Math.max(0, Math.round(total)),
    totalDuration: Math.max(0, Math.round(total)), // alias for charts
    deepMinutes: Math.max(0, Math.round(raw.deepMinutes || raw.sleepDeep || raw.deep_minutes || 0)),
    lightMinutes: Math.max(0, Math.round(raw.lightMinutes || raw.sleepLight || raw.light_minutes || 0)),
    remMinutes: Math.max(0, Math.round(raw.remMinutes || raw.sleepRem || raw.rem_minutes || 0)),
    awakeMinutes: Math.max(0, Math.round(raw.awakeMinutes || raw.sleepAwake || raw.awake_minutes || 0)),
    quality: raw.quality || raw.sleepQuality || null,
  }
}

/**
 * Validates and normalizes heart rate data
 */
export function normalizeHeartRateData(raw: any): {
  bpm: number
  type: string
  timestamp: any
} {
  if (!raw || typeof raw !== 'object') {
    return {
      bpm: 0,
      type: 'unknown',
      timestamp: null,
    }
  }

  const bpm = Math.round(raw.bpm || 0)
  
  return {
    bpm: Math.max(30, Math.min(250, bpm)), // Clamp to valid HR range
    type: raw.type || 'automated',
    timestamp: raw.timestamp || null,
  }
}

/**
 * Validates and normalizes SpO2 data
 */
export function normalizeSpO2Data(raw: any): {
  value: number
  timestamp: any
} {
  if (!raw || typeof raw !== 'object') {
    return {
      value: 0,
      timestamp: null,
    }
  }

  const value = raw.value || raw.spo2 || 0
  
  return {
    value: Math.max(0, Math.min(100, Math.round(value))), // Clamp 0-100%
    timestamp: raw.timestamp || null,
  }
}

/**
 * Validates and normalizes stress data
 */
export function normalizeStressData(raw: any): {
  level: number
  timestamp: any
  stressCategory: 'low' | 'moderate' | 'high' | null
} {
  if (!raw || typeof raw !== 'object') {
    return {
      level: 0,
      timestamp: null,
      stressCategory: null,
    }
  }

  const level = Math.round(raw.level || raw.stress || 0)
  const clampedLevel = Math.max(0, Math.min(100, level))
  
  let stressCategory: 'low' | 'moderate' | 'high' | null = null
  if (clampedLevel <= 30) stressCategory = 'low'
  else if (clampedLevel <= 60) stressCategory = 'moderate'
  else stressCategory = 'high'
  
  return {
    level: clampedLevel,
    timestamp: raw.timestamp || null,
    stressCategory,
  }
}

/**
 * Validates and normalizes blood pressure data
 */
export function normalizeBloodPressureData(raw: any): {
  systolic: number
  diastolic: number
  timestamp: any
  classification: string
} {
  if (!raw || typeof raw !== 'object') {
    return {
      systolic: 0,
      diastolic: 0,
      timestamp: null,
      classification: 'Unknown',
    }
  }

  const systolic = Math.max(70, Math.min(250, Math.round(raw.systolic || 0)))
  const diastolic = Math.max(40, Math.min(150, Math.round(raw.diastolic || 0)))
  
  // AHA Classification
  let classification = 'Normal'
  if (systolic >= 180 || diastolic >= 120) {
    classification = 'Hypertensive Crisis'
  } else if (systolic >= 140 || diastolic >= 90) {
    classification = 'Hypertension Stage 2'
  } else if (systolic >= 130 || diastolic >= 80) {
    classification = 'Hypertension Stage 1'
  } else if (systolic >= 120 && diastolic < 80) {
    classification = 'Elevated'
  }
  
  return {
    systolic,
    diastolic,
    timestamp: raw.timestamp || null,
    classification,
  }
}

/**
 * Validates and normalizes weight data
 */
export function normalizeWeightData(raw: any): {
  weightKg: number
  weight: number // alias
  date: string
  timestamp: any
  note: string | null
} {
  if (!raw || typeof raw !== 'object') {
    return {
      weightKg: 0,
      weight: 0,
      date: '',
      timestamp: null,
      note: null,
    }
  }

  const weightKg = raw.weightKg || raw.weight || raw.weight_kg || 0
  
  return {
    weightKg: Math.max(0, Math.round(weightKg * 10) / 10), // 1 decimal place
    weight: Math.max(0, Math.round(weightKg * 10) / 10), // alias
    date: raw.date || '',
    timestamp: raw.timestamp || null,
    note: raw.note || null,
  }
}

/**
 * Validates and normalizes workout data
 */
export function normalizeWorkoutData(raw: any): {
  type: string
  sportType: string // alias
  durationSeconds: number
  distanceMeters: number
  calories: number
  startTime: any
  stravaUploaded: boolean
} {
  if (!raw || typeof raw !== 'object') {
    return {
      type: 'Unknown',
      sportType: 'Unknown',
      durationSeconds: 0,
      distanceMeters: 0,
      calories: 0,
      startTime: null,
      stravaUploaded: false,
    }
  }

  const type = raw.type || 'Unknown'
  
  return {
    type,
    sportType: type, // alias
    durationSeconds: Math.max(0, Math.round(raw.durationSeconds || raw.duration || 0)),
    distanceMeters: Math.max(0, raw.distanceMeters || raw.distance || 0),
    calories: Math.max(0, Math.round(raw.calories || 0)),
    startTime: raw.startTime || raw.createdAt || null,
    stravaUploaded: raw.stravaUploaded || false,
  }
}

/**
 * Validates and normalizes device data
 */
export function normalizeDeviceData(raw: any, docId: string): {
  name: string
  address: string
  battery: number
  batteryUpdatedAt: any
  lastConnected: any
} {
  if (!raw || typeof raw !== 'object') {
    return {
      name: 'Unknown Device',
      address: docId,
      battery: 0,
      batteryUpdatedAt: null,
      lastConnected: null,
    }
  }

  return {
    name: raw.name || 'Unknown Device',
    address: raw.address || docId,
    battery: Math.max(0, Math.min(100, Math.round(raw.battery || 0))),
    batteryUpdatedAt: raw.batteryUpdatedAt || null,
    lastConnected: raw.lastConnected || raw.lastSync || null,
  }
}

// ============================================
// Error Handling Utilities
// ============================================

export function safeGet<T>(
  obj: any,
  path: string,
  defaultValue: T
): T {
  try {
    const keys = path.split('.')
    let result = obj
    for (const key of keys) {
      if (result === null || result === undefined) {
        return defaultValue
      }
      result = result[key]
    }
    return result !== undefined ? result : defaultValue
  } catch {
    return defaultValue
  }
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

export function round(value: number, decimals: number = 0): number {
  const multiplier = Math.pow(10, decimals)
  return Math.round(value * multiplier) / multiplier
}
